/**
 * Utility functions for getting display names from CRM objects.
 *
 * Display names are available via obj.properties.ahoy_display_name, with
 * fallbacks to ahoy_name and ahoy_title for entity types that don't carry one.
 */

import type { CrmObject, PropertyValue } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { memoContentPreview } from './memoContentPreview';

/**
 * Get the display name for a CRM object.
 *
 * Delegates to getDisplayNameFromProperties, which tries, in order:
 * properties.ahoy_display_name, then properties.ahoy_name (used by pipeline
 * stages and other named entities), then properties.ahoy_title (used by
 * notes, meetings, meeting-notes, and risks — types with no ahoy_name), then
 * a plain-text preview of ahoy_content (covers optimistically-created,
 * title-less notes — mirrors the server display-name formula), then
 * a composed "first last" name from ahoy_first_name/ahoy_last_name (covers
 * optimistically-created contacts, whose server-computed ahoy_display_name
 * hasn't landed yet), falling back to the provided fallback value if none of
 * those produce a non-empty string.
 *
 * For HydratedEntity (timeline lookups), use entity.ahoy_display_name directly
 * instead of this function.
 *
 * @param object - A CrmObject with a properties bag
 * @param fallback - Fallback value if display name is not available (default: 'Unknown')
 * @returns The display name or fallback
 */
export function getDisplayName(
  object: CrmObject,
  fallback = 'Unknown'
): string {
  const fromProperties = getDisplayNameFromProperties(
    object.properties,
    null,
    object.entity_type_name
  );
  if (fromProperties) {
    return fromProperties;
  }

  return fallback;
}

/**
 * Get the display name from properties directly.
 *
 * Useful when you have properties but not a full CrmObject.
 * Handles both new PropertyValue format and legacy raw values.
 *
 * @param properties - The properties record (with PropertyValue wrappers or raw values)
 * @param fallback - Fallback value if display name is not available (default: 'Unknown')
 * @param entityTypeName - The entity's type. The ahoy_content body-preview
 *   fallback applies ONLY to `ahoy_note`, whose server display-name formula is
 *   its body preview. A title-less task is named "Untitled Task" server-side and
 *   never derives its name from ahoy_content, so passing its type (or nothing)
 *   correctly skips the preview branch and falls through to the fallback.
 * @returns The display name or fallback
 */
export function getDisplayNameFromProperties(
  properties: Record<string, PropertyValue | unknown> | null | undefined,
  fallback?: string,
  entityTypeName?: string
): string;
export function getDisplayNameFromProperties(
  properties: Record<string, PropertyValue | unknown> | null | undefined,
  fallback: null,
  entityTypeName?: string
): string | null;
export function getDisplayNameFromProperties(
  properties: Record<string, PropertyValue | unknown> | null | undefined,
  fallback: string | null = 'Unknown',
  entityTypeName?: string
): string | null {
  if (!properties) {
    return fallback;
  }

  const displayNameProp = properties.ahoy_display_name;
  const displayName = getPropertyValue<string>(
    displayNameProp as PropertyValue | string | undefined
  );

  if (typeof displayName === 'string' && displayName.trim()) {
    return displayName.trim();
  }

  // Fallback to ahoy_name (used by pipeline stages and other named entities)
  const nameProp = properties.ahoy_name;
  const name = getPropertyValue<string>(
    nameProp as PropertyValue | string | undefined
  );

  if (typeof name === 'string' && name.trim()) {
    return name.trim();
  }

  // Fallback to ahoy_title (used by notes, meetings, meeting-notes, risks —
  // named entities that carry no ahoy_name). Like ahoy_display_name this is
  // often server-computed and absent from client-side preview projections,
  // so without this branch a projected note renders literal 'Unknown'.
  const titleProp = properties.ahoy_title;
  const title = getPropertyValue<string>(
    titleProp as PropertyValue | string | undefined
  );

  if (typeof title === 'string' && title.trim()) {
    return title.trim();
  }

  // Memo body preview — notes are created WITHOUT a title, so an optimistic
  // note row has neither ahoy_display_name (server formula, not yet stamped)
  // nor ahoy_title, and fell through to the caller's fallback until the create
  // landed — the literal 'Unknown' by default, or 'Unnamed' where the list cell
  // passes that instead. Mirrors the server formula's
  // `text_preview(ahoy_content, 80)` step so the provisional name matches the
  // one the server stamps.
  //
  // Restricted to ahoy_note: only the note's server formula names it by its
  // body. A title-less TASK is "Untitled Task" server-side and never derives
  // its name from ahoy_content, so a task with a description must NOT show that
  // description as its title. When the type is unknown we skip this branch too,
  // so a stray non-note with an ahoy_content value can't be mis-named.
  if (entityTypeName === 'ahoy_note') {
    const contentPreview = memoContentPreview(
      getPropertyValue<string>(
        properties.ahoy_content as PropertyValue | string | undefined
      )
    );

    // The server formula is `ahoy_title or text_preview(ahoy_content, 80) or
    // "Note"` — its final `or "Note"` means a title-less note with an empty or
    // tag-only body is named the literal "Note", never blank. Mirror that here:
    // an ahoy_note that reached this branch has no title, so its name is the
    // body preview or the same literal fallback. Falling through to the caller's
    // 'Unknown'/'Unnamed' fallback instead would make an optimistic empty note
    // render a different name than the one the server stamps a moment later.
    return contentPreview ?? 'Note';
  }

  // Last resort — compose a person name from its parts.
  //
  // `ahoy_display_name` is a SERVER-COMPUTED formula property, so it is absent
  // on an optimistically created entity: the client mints the entity from the
  // create form's raw properties and the formula is only evaluated once the
  // event is committed. Without this fallback an optimistic contact row renders
  // "Unnamed" (and its cells as "—") for the whole round trip, which reads as a
  // broken row rather than a pending one.
  //
  // Server-hydrated objects always carry `ahoy_display_name`, so this branch is
  // unreachable for them — it only ever fills the provisional gap, and the
  // authoritative value supersedes it when the entity lands.
  //
  // Delegates to composePersonName so the "first last" composition rule lives
  // in exactly one place and cannot drift between the two derivations.
  const composed = composePersonName(
    getPropertyValue<string>(
      properties.ahoy_first_name as PropertyValue | string | undefined
    ),
    getPropertyValue<string>(
      properties.ahoy_last_name as PropertyValue | string | undefined
    )
  );

  if (composed) {
    return composed;
  }

  return fallback;
}

/**
 * Compose a person's display name from separate first/last name parts.
 *
 * Mirrors the composition in `getDisplayNameFromProperties` above (used for
 * optimistically-created contacts before the server-computed
 * `ahoy_display_name` lands). Exported for callers that already hold the raw
 * first/last strings — e.g. an inline two-field name editor building its own
 * optimistic `ahoy_display_name` — so they don't have to re-derive the same
 * composition rule.
 */
export function composePersonName(
  first: string | undefined | null,
  last: string | undefined | null
): string {
  return [first, last]
    .filter((p): p is string => typeof p === 'string' && !!p.trim())
    .map(p => p.trim())
    .join(' ');
}
